import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  CircularProgress,
  Pagination,
  Typography,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useTheme, useMediaQuery } from "@mui/material";

const SchedulesTable = ({
  loading,
  ScheduleData,
  handleEditData,
  handleDeleteData,
}) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchField, setSearchField] = useState("fname");

  const handleSearchFieldChange = (event) => {
    setSearchField(event.target.value);
    setSearch(""); // clear previous search input
    setPage(1);
  };

  const filteredData = ScheduleData.filter((row) => {
    if (searchField === "fname") {
      return row.doctor?.fname?.toLowerCase().includes(search.toLowerCase());
    }
    return true;
  });

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Paper elevation={3} sx={{ padding: 2, borderRadius: 3, width: "100%" }}>
      {/* Top Controls */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <Typography variant="subtitle2">
            Total: {filteredData.length}
          </Typography>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 10,
              alignItems: "center",
            }}
          >
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 120, flex: "1 1 150px" }}
            >
              <InputLabel>Search By</InputLabel>
              <Select
                value={searchField}
                onChange={handleSearchFieldChange}
                label="Search By"
              >
                <MenuItem value="fname">Doctor Name</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: "1 1 150px" }}
            />
          </div>
        </div>
      </div>

      {/* Table and Content */}
      {loading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {isSmallScreen ? null : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: "#1976d2" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff" }}>S.No.</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Doctor Name</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Day</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Time Slot</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Slot Duration</TableCell>
                    <TableCell sx={{ color: "#fff" }}>
                      Start and End Period
                    </TableCell>
                    <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
          )}
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <CircularProgress />
          </div>
        </motion.div>
      ) : filteredData.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{ textAlign: "center", padding: "40px 0", fontWeight: "bold" }}
        >
          No Data Found
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {isSmallScreen ? (
            <Box display="flex" flexDirection="column" gap={2}>
              {paginatedData.map((row, index) => (
                <Paper
                  key={row.id}
                  elevation={2}
                  sx={{ padding: 2, borderRadius: 2 }}
                >
                  <Typography fontWeight="bold">
                    {index + 1}. Dr. {row.doctor?.fname || "N/A"}
                  </Typography>
                  <Typography>Day: {row.day_of_week}</Typography>
                  <Typography>
                    Time: {row.start_time?.slice(0, 5)} -{" "}
                    {row.end_time?.slice(0, 5)}
                  </Typography>
                  <Typography>
                    Slot Duration: {row.slot_duration} mins
                  </Typography>
                  <Typography>
                    Period: {row.start_date} -{row.end_date}
                  </Typography>
                  <Typography>Status: {row.is_active ? "✅" : "❌"}</Typography>
                  <Typography>online Doctor: {row.online ? "✅" : "❌"}</Typography>

                  <Box mt={2} display="flex" gap={1}>
                    <IconButton
                      color="secondary"
                      onClick={() => handleEditData(row)}
                    >
                      <Edit />
                    </IconButton>
                    {/* <IconButton
                      color="error"
                      onClick={() => handleDeleteData(row)}
                    >
                      <Delete />
                    </IconButton> */}
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: "#1976d2" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff" }}>S.No.</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Doctor Name</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Day</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Time Slot</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Slot Duration</TableCell>
                    <TableCell sx={{ color: "#fff" }}> Period</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                    <TableCell sx={{ color: "#fff" }}>online Doctor</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row, index) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{row.doctor?.fname || "N/A"}</TableCell>
                      <TableCell>{row.day_of_week}</TableCell>
                      <TableCell>
                        {row.start_time?.slice(0, 5)} -{" "}
                        {row.end_time?.slice(0, 5)}
                      </TableCell>

                      <TableCell>{row.slot_duration} mins</TableCell>
                      <TableCell>
                        {row.start_date} -{row.end_date}
                      </TableCell>
                      <TableCell>{row.is_active ? "✅" : "❌"}</TableCell>
                      <TableCell>{row.online ? "✅" : "❌"}</TableCell>

                      <TableCell>
                        <IconButton
                          color="secondary"
                          onClick={() => handleEditData(row)}
                        >
                          <Edit />
                        </IconButton>
                        {/* <IconButton
                          color="error"
                          onClick={() => handleDeleteData(row)}
                        >
                          <Delete />
                        </IconButton> */}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: 16,
            }}
          >
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </div>
        </motion.div>
      )}
    </Paper>
  );
};

export default SchedulesTable;
